import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  TrendingUp, ChevronLeft, Shield, Eye, LogOut, Crown, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { InfoTooltip } from "../components/ui/info-tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Infobulles pour les paramètres
const SETTINGS_TOOLTIPS = {
  incomeCap: "Active ou désactive la limitation automatique sur votre secteur d'emploi. Recommandé : activé pour éviter une double exposition en cas de crise.",
  incomeCapSlider: "Pourcentage maximum que vous autorisez sur votre secteur d'emploi. Par défaut 15%. Réduire = plus prudent. Augmenter = plus exposé.",
  detailLevel: "Essentiel : résumés courts sans chiffres. Complet : données détaillées avec pourcentages et probabilités.",
  premium: "Fonctionnalité réservée aux abonnés Premium (199€/an). Accès illimité aux briefings et questions.",
};

export default function SettingsPage({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    income_cap_enabled: user?.profile?.income_cap_enabled ?? true,
    income_cap_percentage: user?.profile?.income_cap_percentage ?? 15,
    detail_level: user?.profile?.detail_level ?? "essentiel"
  });

  const handleSave = async (field, value) => {
    setLoading(true);
    try {
      const response = await axios.patch(`${API}/settings`, {
        [field]: value
      });
      setUser(response.data);
      setSettings(prev => ({ ...prev, [field]: value }));
      toast.success("Paramètres mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Erreur lors de la création de la session de paiement");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              data-testid="back-btn"
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="font-bold tracking-tight font-manrope">Paramètres</span>
          </div>
          
          {user?.is_premium && (
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
              <Crown className="w-3 h-3 mr-1" />
              Premium
            </Badge>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Account info */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-manrope">Compte</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-12 h-12 rounded-full"
                />
              )}
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium status / Upgrade */}
        {!user?.is_premium && (
          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-lg font-manrope flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Passer à Premium
              </CardTitle>
              <CardDescription>
                Débloquez les briefings et questions illimités
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold font-manrope">199€<span className="text-sm text-muted-foreground">/an</span></p>
                  <p className="text-sm text-muted-foreground">Soit moins de 17€/mois</p>
                </div>
                <Button 
                  data-testid="upgrade-btn"
                  onClick={handleUpgrade}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-sm"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Souscrire
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income Cap Protection */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-manrope flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Protection Income Cap
              <InfoTooltip content={SETTINGS_TOOLTIPS.incomeCap} />
            </CardTitle>
            <CardDescription>
              Limitez automatiquement votre exposition au secteur de votre emploi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="income-cap-toggle" className="flex flex-col">
                <span>Activer la protection</span>
                <span className="text-sm text-muted-foreground font-normal">
                  {user?.profile?.employment_sector 
                    ? `Secteur : ${user.profile.employment_sector}`
                    : "Aucun secteur d'emploi défini"
                  }
                </span>
              </Label>
              <Switch
                id="income-cap-toggle"
                data-testid="income-cap-toggle"
                checked={settings.income_cap_enabled}
                onCheckedChange={(checked) => handleSave("income_cap_enabled", checked)}
                disabled={loading || !user?.profile?.employment_sector}
              />
            </div>

            {/* Percentage slider */}
            {settings.income_cap_enabled && user?.profile?.employment_sector && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Label>Plafond d'exposition</Label>
                    <InfoTooltip content={SETTINGS_TOOLTIPS.incomeCapSlider} />
                  </div>
                  <span className="font-mono font-semibold">{settings.income_cap_percentage}%</span>
                </div>
                <Slider
                  data-testid="income-cap-slider"
                  value={[settings.income_cap_percentage]}
                  onValueCommit={(value) => handleSave("income_cap_percentage", value[0])}
                  min={5}
                  max={50}
                  step={5}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  Votre exposition au secteur {user.profile.employment_sector} sera limitée à {settings.income_cap_percentage}% maximum dans les recommandations.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail level */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-manrope flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Niveau de détail
              <InfoTooltip content={SETTINGS_TOOLTIPS.detailLevel} />
            </CardTitle>
            <CardDescription>
              Ajustez la complexité des briefings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={settings.detail_level}
              onValueChange={(value) => handleSave("detail_level", value)}
              disabled={loading}
            >
              <div className="flex items-start space-x-3 p-3 rounded-sm border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="essentiel" id="essentiel" data-testid="detail-essentiel" className="mt-1" />
                <Label htmlFor="essentiel" className="flex flex-col cursor-pointer">
                  <span className="font-medium">Essentiel</span>
                  <span className="text-sm text-muted-foreground font-normal">Résumé clair et concis, sans jargon technique</span>
                </Label>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-sm border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="complet" id="complet" data-testid="detail-complet" className="mt-1" />
                <Label htmlFor="complet" className="flex flex-col cursor-pointer">
                  <span className="font-medium">Complet</span>
                  <span className="text-sm text-muted-foreground font-normal">Données détaillées avec pourcentages et analyse approfondie</span>
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Button 
              data-testid="logout-btn"
              variant="outline" 
              onClick={handleLogout}
              className="w-full gap-2"
            >
              <LogOut className="w-4 h-4" />
              Se déconnecter
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-xs text-center text-muted-foreground">
          Oracle Portfolio Advisor v1.0.0<br />
          Les informations fournies sont à caractère éducatif uniquement.
        </p>
      </main>
    </div>
  );
}
